import { Form, Formik, type FormikHelpers } from "formik";
import { useState } from "react";
import { FiArrowRight, FiEye, FiEyeOff, FiUnlock, FiUser } from "react-icons/fi";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { acceptInvitation } from "@/features/team/api";
import { applyApiErrors } from "@/lib/form-errors";

const schema = Yup.object({
  name: Yup.string().trim().max(255).required("Your name is required"),
  password: Yup.string().min(8, "Min 8 characters").required("Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password")], "Passwords must match")
    .required("Confirm your password"),
});

interface AcceptValues {
  name: string;
  password: string;
  confirmPassword: string;
}

/**
 * Public "accept invitation" form. The token comes from the emailed link's
 * `?token=` query param; the invitee sets their name + password and lands
 * signed in (the backend returns a session).
 */
export function AcceptInviteForm() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  async function handleSubmit(values: AcceptValues, { setErrors }: FormikHelpers<AcceptValues>) {
    try {
      await acceptInvitation(token, values.password, values.name.trim());
      toast.success("Welcome aboard!");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      // Bind server-side field errors (e.g. a rejected password) to the field.
      toast.error(
        applyApiErrors(err, { setErrors }, { fallback: "Couldn't accept the invitation." }),
      );
    }
  }

  if (!token) {
    return (
      <Card className="gap-0 p-6 sm:p-10">
        <h1 className="font-heading text-foreground mb-2 text-2xl font-bold">Invitation link</h1>
        <p className="text-muted-foreground">
          This invitation link is missing its token. Please open the most recent link from your
          invitation email.
        </p>
        <p className="text-muted-foreground mt-6 text-sm">
          Already have an account?{" "}
          <Link to="/login" className="text-primary hover:text-primary/80 font-medium">
            Sign in
          </Link>
        </p>
      </Card>
    );
  }

  return (
    <>
      <Card className="gap-0 p-6 sm:p-10">
        <div className="mb-10">
          <h1 className="font-heading text-foreground mb-2 text-2xl font-bold">
            Accept your invitation
          </h1>
          <p className="text-muted-foreground">Set your name and a password to get started.</p>
        </div>

        <Formik<AcceptValues>
          initialValues={{ name: "", password: "", confirmPassword: "" }}
          validationSchema={schema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="grid gap-6">
              <FormField
                name="name"
                label="Full Name"
                autoComplete="name"
                placeholder="Jane Preparer"
                icon={<FiUser className="size-4" />}
                className="h-11"
              />
              <FormField
                name="password"
                label="Password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                placeholder="••••••••"
                icon={<FiUnlock className="size-4" />}
                className="h-11"
                endAdornment={
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <FiEye className="size-4" /> : <FiEyeOff className="size-4" />}
                  </button>
                }
              />
              <FormField
                name="confirmPassword"
                label="Confirm Password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                placeholder="••••••••"
                icon={<FiUnlock className="size-4" />}
                className="h-11"
              />
              <Button type="submit" disabled={isSubmitting} className="h-11 gap-2">
                {isSubmitting ? "Setting up…" : "Accept & continue"}
                <FiArrowRight className="size-4" />
              </Button>
            </Form>
          )}
        </Formik>
      </Card>

      <p className="text-muted-foreground mt-8 text-center text-sm">
        Already have an account?{" "}
        <Link to="/login" className="text-primary hover:text-primary/80 font-medium transition-colors">
          Sign in
        </Link>
      </p>
    </>
  );
}
